import {useNavigate, useParams} from 'react-router-dom';
import Loading from 'shared/migration-components/loading';
import styled, {useTheme} from 'styled-components';
import dateFnsService from 'shared/services/date-fns-service';
import toBrl from 'shared/util/to-brl';
import {
  IoArrowBack,
  IoLockClosed,
  IoPersonCircleOutline,
  IoStar,
} from 'react-icons/io5';
import useDeliveryManBilling from '../hooks/use-billing';

import {PiForkKnifeFill} from 'react-icons/pi';

import {MdOutlineDeliveryDining} from 'react-icons/md';

import Table from 'shared/migration-components/table';
import {FaHourglassEnd, FaHourglassStart} from 'react-icons/fa';
import PaidDeliveryManBillingSelect from '../components/paid-delivery-man-billing-select';
import BillingSection from 'modules/billings/components/billing-section';
import useDeliveryManBillingOccurrences from '../hooks/use-delivery-man-billing-occurrences';
import {
  OccurenceTargetEnum,
  OccurrenceProps,
  translatedOccurrenceProps,
} from 'modules/orders/types';

const DeliveryManBillingDetails: React.FC = () => {
  const {id} = useParams();
  const {data, isLoading} = useDeliveryManBilling(id!);
  const billingOccurrencesQuery = useDeliveryManBillingOccurrences(id!);

  const {colors} = useTheme();
  const navigate = useNavigate();

  if (isLoading)
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
        }}>
        <Loading size={22} />
      </div>
    );

  return (
    <Container>
      <Column style={{padding: 15, paddingTop: 0}}>
        <Content>
          <Info>
            <Column>
              <BackButton onClick={() => navigate(-1)}>
                <IoArrowBack color={colors.text} />
                <BackTitle>Voltar</BackTitle>
              </BackButton>
              <Title>Fatura #</Title>
              <Description>Uau! Delivery</Description>
            </Column>
            <Ratting>
              <Column
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                {data.deliveryMan.profilePicture && (
                  <img
                    style={{borderRadius: 25}}
                    width={50}
                    height={50}
                    src={data.deliveryMan.profilePicture}
                  />
                )}
                {!data.deliveryMan.profilePicture && (
                  <IoPersonCircleOutline size={40} color={colors.darkGray} />
                )}
              </Column>
              <Column>
                <LeadingText>{data.deliveryMan.name}</LeadingText>
                <Text>{data.deliveryMan.phone}</Text>
                <Text>Chave pix: {data.deliveryMan.pixKey}</Text>
              </Column>
              <Column style={{alignItems: 'center', gap: 3}}>
                <LeadingText style={{textAlign: 'center', fontSize: 20}}>
                  {data.deliveryMan.ratting ?? 'N/A'}
                </LeadingText>
                {data.deliveryMan.ratting && (
                  <Row>
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                  </Row>
                )}
              </Column>
              <Column style={{alignItems: 'center'}}>
                <LeadingText style={{textAlign: 'center', fontSize: 20}}>
                  {data.deliveryMan.numberOfDeliveries}
                </LeadingText>
                <Text>avaliações</Text>
              </Column>
            </Ratting>
            <Title>Sobre a fatura</Title>
            <List>
              <Row style={{alignItems: 'start', gap: 10}}>
                <FaHourglassStart size={20} />
                <Column>
                  <Text>Data de início</Text>
                  <RegularText>
                    {dateFnsService.dateFormat(data.startDate)}
                  </RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <FaHourglassEnd size={20} />
                <Column>
                  <Text>Data de fim</Text>
                  <RegularText>
                    {dateFnsService.dateFormat(data.endDate)}
                  </RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <IoLockClosed size={20} />
                <Column>
                  <Text>Fechada?</Text>
                  <RegularText>{data.isClosed ? 'Sim' : 'Não'}</RegularText>
                </Column>
              </Row>
              <PaidDeliveryManBillingSelect {...data} />
              <Row style={{alignItems: 'start', gap: 10}}>
                <PiForkKnifeFill size={20} />
                <Column>
                  <Text>Quantidade de pedidos</Text>
                  <RegularText>{data.numberOfOrders}</RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <MdOutlineDeliveryDining size={20} />
                <Column>
                  <Text>Total em entregas</Text>
                  <RegularText>{toBrl(data.deliveries)}</RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <MdOutlineDeliveryDining size={20} />
                <Column>
                  <Text>Taxa do entregador</Text>
                  <RegularText>{toBrl(data.deliveryManFee)}</RegularText>
                </Column>
              </Row>
            </List>
            <Line />
          </Info>
          <SideBar>
            <Checkout>
              <Title>Resumo da fatura</Title>
              <Row style={{justifyContent: 'space-between'}}>
                <Text>Total bruto</Text>
                <Text>{toBrl(data.total)}</Text>
              </Row>
              <Row style={{justifyContent: 'space-between'}}>
                <Text>Total em entregas</Text>
                <Text>{toBrl(data.deliveries)}</Text>
              </Row>
              <Row style={{justifyContent: 'space-between'}}>
                <Text>Taxa do entregador</Text>
                <Text>{toBrl(data.deliveryManFee)}</Text>
              </Row>
              <BillingSection
                {...{
                  label: 'Ocorrências',
                  value: data.occurrences,
                  variant: data.occurrences > 0 ? 'green' : 'red',
                  tooltip:
                    'Valores extras no processo do pedido. Ex: entrega extra, seguro de pedido e etc...',
                }}
              />
              <Row style={{justifyContent: 'space-between'}}>
                <Text style={{fontSize: 16, fontWeight: 500}}>
                  Total a receber
                </Text>
                <Text
                  style={{
                    color: colors.success,
                    fontSize: 16,
                    fontWeight: 500,
                  }}>
                  {toBrl(data.deliveryManAmount)}
                </Text>
              </Row>
            </Checkout>
          </SideBar>
        </Content>
        <Column style={{gap: 25, marginTop: 25}}>
          <Column style={{padding: '0 15px', marginTop: -15}}>
            {billingOccurrencesQuery.data?.length! > 0 && (
              <Column>
                <Title>Ocorrências</Title>
                <Table<OccurrenceProps>
                  {...{
                    isLoading: billingOccurrencesQuery.isLoading,
                    data: billingOccurrencesQuery.data,
                  }}
                  translatedPropsKey={translatedOccurrenceProps}
                  onRowClick={(row) =>
                    window.open(`/orders/${row.orderId}`, '_blank')
                  }
                  options={[
                    'description',
                    'observation',
                    {
                      value: 'value',
                      column: (row) => (
                        <RegularText
                          style={{
                            fontSize: 12,
                            color:
                              row.payer === OccurenceTargetEnum.DELIVERY_MAN
                                ? colors.red
                                : colors.success,
                          }}>
                          {toBrl(
                            row.value *
                              (row.payer === OccurenceTargetEnum.DELIVERY_MAN
                                ? -1
                                : 1),
                          )}
                        </RegularText>
                      ),
                    },
                    {
                      value: 'createdAt',
                      column: (row) =>
                        dateFnsService.dateTimeFormat(row.createdAt),
                    },
                  ]}
                />
              </Column>
            )}
          </Column>
          <Column style={{padding: '0 15px', marginTop: -15}}>
            <Title>Pedidos</Title>
            <Table
              translatedPropsKey="OrderProps"
              data={data.orders}
              options={[
                'number',
                {
                  value: 'user.name',
                  label: 'Cliente',
                  column: (row) => row.guestUser?.name ?? row.user?.name,
                },
                {
                  value: 'createdAt',
                  column: (row) => dateFnsService.dateTimeFormat(row.createdAt),
                },
                {
                  value: 'paidTotal',
                  column: (row) => toBrl(row.paidTotal),
                },
                {
                  value: 'deliveryPrice',
                  column: (row) => toBrl(row.totalDeliveryPrice),
                },
                {
                  value: 'deliveryManFee',
                  column: (row) => toBrl(row.deliveryManFee),
                },
              ]}
            />
          </Column>
        </Column>
      </Column>
    </Container>
  );
};

export default DeliveryManBillingDetails;

const Container = styled.div`
  max-width: 100vw;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.card};
  color: ${(props) => props.theme.colors.text};
`;

export const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  margin-bottom: 15px;

  svg {
    font-size: 24px;
  }
`;

export const BackTitle = styled.h1`
  color: ${(props) => props.theme.colors.text};
  font-size: 18px;
`;

const Content = styled.div`
  width: 1100px;
  display: grid;
  grid-template-columns: 680px 1fr;
  gap: 30px;
`;

const Info = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SideBar = styled.div`
  padding: 15px;
  padding-top: 60px;
  position: sticky;
  top: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Checkout = styled.div`
  border: 1px solid rgba(128, 128, 128, 0.4);
  box-shadow: rgba(0, 0, 0, 0.12) 0px 6px 16px;
  border-radius: 10px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
`;

const Description = styled.p``;

const LeadingText = styled.h3`
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
  line-height: normal;
`;

const Text = styled.text`
  color: ${(props) => props.theme.colors.text};
  font-weight: 300;
  line-height: normal;
`;

const RegularText = styled.text`
  color: ${(props) => props.theme.colors.text};
  font-weight: 500;
`;

const Line = styled.div`
  height: 0.5px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.border};
`;

const Ratting = styled.div`
  border: 1px solid ${(props) => props.theme.colors.border};
  padding: 20px 15px;
  border-radius: 15px;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr;
  gap: 15px;
  align-items: center;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
`;
